import { api } from './client'

export interface Role {
  id: number
  code: string
  name: string
  description: string
  color: string
  user_count: number
}

/** @deprecated Use role.color from backend instead */
export const ROLE_COLORS: Record<string, string> = {
  admin: 'red',
  devops: 'orange',
  developer: 'yellow',
  manager: 'green',
  suporte: 'cyan',
  viewer: 'gray',
}

/** Um ponto do codigo onde a funcao decide acesso. Derivado por `role_gates.py`. */
export interface RoleGateQuantidade {
  chave: string
  valor: number | null
  de: number | null
  /** Quantas das escondidas seguem alcancaveis pela porta unica. */
  ainda_alcancaveis?: number | null
}

export interface RoleGate {
  superficie: string
  efeito: string
  onde: string
  roles: string[]
  /** Caminho que devolve o acesso sem a funcao, quando existe. */
  compensado_por: string | null
  /** Funcoes/flags que alguem suporia conceder e nao concedem. */
  nao_concede: string[]
  quantidade: RoleGateQuantidade | null
}

export interface RoleGatesResponse {
  role_code: string
  gates: RoleGate[]
  snapshot: Record<string, number>
}

/** O portao EFETIVO de uma acao do chat, respeitando os desvios do codigo. */
export interface AcaoGate {
  acao: string
  label: string
  permissao: string | null
  funcoes_exigidas: string[]
  quem_decide: 'funcao_e_permissao' | 'todos' | 'permissao_de_chamados'
  motivo: string
  caminho_alternativo: string | null
}

export interface AcoesGateResponse {
  acoes: AcaoGate[]
  permissoes_que_exigem_funcao: Record<string, string[]>
}

/**
 * Opcoes da consulta das acoes, com a chave de cache no lugar onde o acesso a dado mora.
 *
 * Duas telas consomem isto (Funcoes e Permissoes). A chave repetida a mao nas duas viraria
 * duas caches para a mesma resposta na primeira vez que alguem escrevesse uma delas
 * diferente.
 */
export const acoesGatesQuery = {
  queryKey: ['role-gates-acoes'] as const,
  queryFn: () => rolesApi.getAcoesGates(),
}

export const rolesApi = {
  listRoles: () =>
    api.get<{ items: Role[] }>('/v1/roles').then((r) => r.data.items),

  /** Onde esta funcao decide acesso. Somente leitura — nao existe eixo funcao<->permissao. */
  getRoleGates: (roleCode: string) =>
    api.get<RoleGatesResponse>(`/v1/roles/${roleCode}/gates`).then((r) => r.data),

  /** O que decide cada acao do chat, e quais permissoes ainda exigem funcao. */
  getAcoesGates: () =>
    api.get<AcoesGateResponse>('/v1/role-gates/acoes').then((r) => r.data),

  createRole: (code: string, name: string, description: string, color: string) =>
    api.post<{ id: number; code: string }>('/v1/roles', { code, name, description, color }).then((r) => r.data),

  updateRole: (id: number, data: { name?: string; description?: string; color?: string }) =>
    api.put<{ status: string }>(`/v1/roles/${id}`, data).then((r) => r.data),

  deleteRole: (id: number) =>
    api.delete(`/v1/roles/${id}`),

  getUserRoles: (userId: number) =>
    api
      .get<{ user_id: number; roles: string[] }>(`/v1/users/${userId}/roles`)
      .then((r) => r.data.roles),

  assignRole: (userId: number, roleCode: string, grantedBy: string) =>
    api
      .post(`/v1/users/${userId}/roles`, { role_code: roleCode, granted_by: grantedBy })
      .then((r) => r.data),

  revokeRole: (userId: number, roleCode: string) =>
    api
      .delete(`/v1/users/${userId}/roles/${roleCode}`)
      .then((r) => r.data),
}
